import type { ClassSummary } from '@school-erp/types';
import {
  EXAM_TYPES,
  createExamPaperSchema,
  createExamSchema,
  type ExamStatus,
} from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  HiddenFieldErrors,
  Notice,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import type { Scale } from './GradingTab';

export interface Exam {
  id: string;
  name: string;
  examType: string;
  startDate: string;
  endDate: string;
  status: ExamStatus;
  gradingScaleName: string;
  paperCount: number;
}
interface Paper {
  id: string;
  classId: string;
  className: string;
  subjectId: string;
  subjectName: string;
  examDate: string;
  maxMarks: number;
  passMarks: number;
}
interface ExamDetail extends Exam {
  papers: Paper[];
  history: { fromState: string | null; toState: string; note: string | null; createdAt: string }[];
}
interface Subject {
  id: string;
  name: string;
}

const issues = (list: { path: PropertyKey[]; message: string }[]) =>
  Object.fromEntries(list.map((i) => [String(i.path[0]), i.message]));

/** What may happen next, and who is allowed to do it (the API enforces the same rules). */
const NEXT: Record<
  ExamStatus,
  { to: ExamStatus; permission: string; label: MessageKey; reason?: boolean }[]
> = {
  draft: [{ to: 'scheduled', permission: 'exams.open', label: 'exam.action.scheduled' }],
  scheduled: [
    { to: 'marking', permission: 'exams.open', label: 'exam.action.marking' },
    { to: 'draft', permission: 'exams.open', label: 'exam.action.draft' },
  ],
  marking: [{ to: 'verified', permission: 'exams.verify', label: 'exam.action.verified' }],
  verified: [
    { to: 'published', permission: 'exams.publish', label: 'exam.action.published' },
    {
      to: 'marking',
      permission: 'exams.verify',
      label: 'exam.action.marking.reopen',
      reason: true,
    },
  ],
  published: [
    {
      to: 'marking',
      permission: 'exams.reopen',
      label: 'exam.action.marking.reopen',
      reason: true,
    },
  ],
};

export function ExamsTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    examType: 'term',
    startDate: '',
    endDate: '',
    gradingScaleId: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const exams = useQuery({
    queryKey: ['exams', schoolId],
    queryFn: () => api<Exam[]>(`/schools/${schoolId}/exams`),
  });
  const scales = useQuery({
    queryKey: ['grading', schoolId],
    queryFn: () => api<Scale[]>(`/schools/${schoolId}/grading-scales`),
    enabled: can('grading.view', schoolId),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['exams', schoolId] });
  };
  const create = useMutation({
    mutationFn: (body: object) => api<Exam>(`/schools/${schoolId}/exams`, { method: 'POST', body }),
    onSuccess: (exam) => {
      setForm({ ...form, name: '' });
      setMessage(null);
      setSelected(exam.id);
      refresh();
    },
    onError: (e) => setMessage(errorMessage(e)),
  });

  return (
    <section aria-label={t('exam.tab.exams')} className="space-y-4">
      {message && <Notice kind="error">{message}</Notice>}
      {can('exams.create', schoolId) && (
        <form
          noValidate
          aria-label={t('exam.create')}
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = createExamSchema.safeParse(
              Object.fromEntries(Object.entries(form).filter(([, v]) => v !== '')),
            );
            if (!parsed.success) return setErrors(issues(parsed.error.issues));
            setErrors({});
            create.mutate(parsed.data);
          }}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
        >
          <TextField
            label={t('exam.name')}
            value={form.name}
            error={errors.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div>
            <label htmlFor="exam-type" className="block text-sm font-medium">
              {t('exam.type')}
            </label>
            <select
              id="exam-type"
              value={form.examType}
              onChange={(e) => setForm({ ...form, examType: e.target.value })}
              className={selectClass}
            >
              {EXAM_TYPES.map((x) => (
                <option key={x} value={x}>
                  {t(`exam.type.${x}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="exam-scale" className="block text-sm font-medium">
              {t('exam.scale')}
            </label>
            <select
              id="exam-scale"
              value={form.gradingScaleId}
              onChange={(e) => setForm({ ...form, gradingScaleId: e.target.value })}
              className={selectClass}
            >
              <option value="">{t('marks.choose')}</option>
              {scales.data?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {errors.gradingScaleId && (
              <p role="alert" className="text-xs text-red-700">
                {errors.gradingScaleId}
              </p>
            )}
          </div>
          <TextField
            label={t('exam.start')}
            type="date"
            value={form.startDate}
            error={errors.startDate}
            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
          />
          <TextField
            label={t('exam.end')}
            type="date"
            value={form.endDate}
            error={errors.endDate}
            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
          />
          <div className="self-end">
            <button type="submit" className={primaryButton} disabled={create.isPending}>
              {t('exam.create')}
            </button>
          </div>
          <HiddenFieldErrors
            errors={errors}
            visible={['name', 'startDate', 'endDate', 'gradingScaleId']}
          />
        </form>
      )}

      {exams.data?.length === 0 && <p className="text-slate-600">{t('exam.none')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {exams.data?.map((x) => (
          <li key={x.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
            <span>
              <span className="font-medium">{x.name}</span>{' '}
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">
                {t(`exam.status.${x.status}` as MessageKey)}
              </span>{' '}
              <span className="text-slate-600">
                {x.startDate} → {x.endDate}
              </span>
            </span>
            <button
              type="button"
              className={secondaryButton}
              aria-label={`${t('exam.open')}: ${x.name}`}
              onClick={() => setSelected(x.id)}
            >
              {t('exam.open')}
            </button>
          </li>
        ))}
      </ul>

      {selected && (
        <ExamDetailPanel key={selected} schoolId={schoolId} examId={selected} onChanged={refresh} />
      )}
    </section>
  );
}

function ExamDetailPanel({
  schoolId,
  examId,
  onChanged,
}: {
  schoolId: string;
  examId: string;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const key = ['exams', schoolId, examId];
  const [message, setMessage] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [paper, setPaper] = useState({
    classId: '',
    subjectId: '',
    examDate: '',
    maxMarks: '100',
    passMarks: '35',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const detail = useQuery({
    queryKey: key,
    queryFn: () => api<ExamDetail>(`/schools/${schoolId}/exams/${examId}`),
  });
  const classes = useQuery({
    queryKey: ['classes', schoolId],
    queryFn: () => api<ClassSummary[]>(`/schools/${schoolId}/classes`),
  });
  const subjects = useQuery({
    queryKey: ['subjects', schoolId],
    queryFn: () => api<Subject[]>(`/schools/${schoolId}/subjects`),
    enabled: can('subjects.view', schoolId),
  });
  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    onChanged();
  };
  const move = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/exams/${examId}/transition`, { method: 'POST', body }),
    onSuccess: () => {
      setMessage(null);
      setReason('');
      reload();
    },
    onError: (e) => setMessage(errorMessage(e)),
  });
  const addPaper = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/exams/${examId}/papers`, { method: 'POST', body }),
    onSuccess: () => {
      setMessage(null);
      reload();
    },
    onError: (e) => setMessage(errorMessage(e)),
  });
  const removePaper = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/exams/${examId}/papers/${id}`, { method: 'DELETE' }),
    onSuccess: reload,
    onError: (e) => setMessage(errorMessage(e)),
  });

  if (detail.isPending) return <p role="status">{t('common.loading')}</p>;
  if (detail.isError) return <Notice kind="error">{errorMessage(detail.error)}</Notice>;
  const exam = detail.data;
  const editable = exam.status === 'draft' || exam.status === 'scheduled';
  const actions = NEXT[exam.status].filter((a) => can(a.permission, schoolId));
  const needsReason = actions.some((a) => a.reason);

  return (
    <section
      aria-label={exam.name}
      className="space-y-4 rounded-lg border border-slate-200 bg-white p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{exam.name}</h2>
        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium">
          {t(`exam.status.${exam.status}` as MessageKey)}
        </span>
      </div>
      <p className="text-sm text-slate-600">
        {exam.startDate} → {exam.endDate} · {exam.gradingScaleName}
      </p>
      {message && <Notice kind="error">{message}</Notice>}

      {actions.length > 0 && (
        <div className="flex flex-wrap items-end gap-2">
          {needsReason && (
            <TextField
              className="min-w-64 flex-1"
              label={t('exam.reason')}
              value={reason}
              maxLength={300}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
          {actions.map((a) => (
            <button
              key={`${a.to}-${a.label}`}
              type="button"
              className={a.to === 'published' ? primaryButton : secondaryButton}
              disabled={move.isPending || (a.reason === true && !reason.trim())}
              onClick={() => move.mutate({ to: a.to, ...(a.reason ? { reason } : {}) })}
            >
              {t(a.label)}
            </button>
          ))}
        </div>
      )}

      <h3 className="font-semibold">{t('exam.papers')}</h3>
      {exam.papers.length === 0 && <p className="text-slate-600">{t('exam.noPapers')}</p>}
      {exam.papers.length > 0 && (
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase text-slate-600">
            <tr>
              <th scope="col" className="p-2">
                {t('exam.paper.class')}
              </th>
              <th scope="col" className="p-2">
                {t('exam.paper.subject')}
              </th>
              <th scope="col" className="p-2">
                {t('exam.paper.date')}
              </th>
              <th scope="col" className="p-2">
                {t('exam.paper.max')}
              </th>
              <th scope="col" className="p-2">
                {t('exam.paper.pass')}
              </th>
              <th scope="col" className="p-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {exam.papers.map((p) => (
              <tr key={p.id}>
                <td className="p-2">{p.className}</td>
                <td className="p-2">{p.subjectName}</td>
                <td className="p-2">{p.examDate}</td>
                <td className="p-2">{p.maxMarks}</td>
                <td className="p-2">{p.passMarks}</td>
                <td className="p-2 text-right">
                  {editable && can('exams.update', schoolId) && (
                    <button
                      type="button"
                      className="text-xs text-red-700 underline"
                      aria-label={`${t('exam.paper.remove')}: ${p.subjectName} ${p.className}`}
                      onClick={() => removePaper.mutate(p.id)}
                    >
                      {t('exam.paper.remove')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editable && can('exams.update', schoolId) && (
        <form
          noValidate
          aria-label={t('exam.paper.add')}
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = createExamPaperSchema.safeParse({
              classId: paper.classId,
              subjectId: paper.subjectId,
              examDate: paper.examDate,
              maxMarks: paper.maxMarks === '' ? NaN : Number(paper.maxMarks),
              passMarks: paper.passMarks === '' ? NaN : Number(paper.passMarks),
            });
            if (!parsed.success) return setErrors(issues(parsed.error.issues));
            setErrors({});
            addPaper.mutate(parsed.data);
          }}
          className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6"
        >
          <div>
            <label htmlFor="paper-class" className="block text-sm font-medium">
              {t('exam.paper.class')}
            </label>
            <select
              id="paper-class"
              value={paper.classId}
              onChange={(e) => setPaper({ ...paper, classId: e.target.value })}
              className={selectClass}
            >
              <option value="">{t('marks.choose')}</option>
              {classes.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="paper-subject" className="block text-sm font-medium">
              {t('exam.paper.subject')}
            </label>
            <select
              id="paper-subject"
              value={paper.subjectId}
              onChange={(e) => setPaper({ ...paper, subjectId: e.target.value })}
              className={selectClass}
            >
              <option value="">{t('marks.choose')}</option>
              {subjects.data?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <TextField
            label={t('exam.paper.date')}
            type="date"
            value={paper.examDate}
            error={errors.examDate}
            onChange={(e) => setPaper({ ...paper, examDate: e.target.value })}
          />
          <TextField
            label={t('exam.paper.max')}
            type="number"
            value={paper.maxMarks}
            error={errors.maxMarks}
            onChange={(e) => setPaper({ ...paper, maxMarks: e.target.value })}
          />
          <TextField
            label={t('exam.paper.pass')}
            type="number"
            value={paper.passMarks}
            error={errors.passMarks}
            onChange={(e) => setPaper({ ...paper, passMarks: e.target.value })}
          />
          <div className="self-end">
            <button type="submit" className={primaryButton} disabled={addPaper.isPending}>
              {t('exam.paper.add')}
            </button>
          </div>
          <HiddenFieldErrors errors={errors} visible={['examDate', 'maxMarks', 'passMarks']} />
        </form>
      )}

      {exam.history.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer font-medium">{t('exam.history')}</summary>
          <ul className="mt-2 space-y-1 text-slate-700">
            {exam.history.map((h, i) => (
              <li key={i}>
                {h.createdAt.slice(0, 10)}: {h.fromState ?? '—'} → {h.toState}
                {h.note ? ` (${h.note})` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
